// src/analytics/analyticsClient.js
// Central analytics client module for Google Analytics 4

let isInitialized = false;
let measurementId = null;

/**
 * Injects gtag.js script if not already present
 * @param {string} measurementId - GA4 Measurement ID
 */
function injectGtagScript(measurementId) {
  // Check if script already exists
  if (document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`)) {
    return;
  }

  // Inject gtag.js script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  document.head.appendChild(script1);

  // Initialize dataLayer and gtag function
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;

  // Configure GA4
  gtag('js', new Date());
  gtag('config', measurementId, {
    send_page_view: false // We'll handle page views manually if needed
  });
}

/**
 * Initializes Google Analytics 4
 * @param {string} id - GA4 Measurement ID (format: G-XXXXXXXXXX)
 */
export function initAnalytics(id) {
  const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
  
  if (!id || typeof id !== 'string') {
    console.warn('[Analytics] ⚠️ Measurement ID is required to initialize Analytics. Received:', id);
    return;
  }

  if (isInitialized && measurementId === id) {
    if (isDev) {
      console.log('[Analytics] ℹ️ Analytics already initialized with Measurement ID:', id);
    }
    return;
  }

  try {
    measurementId = id;
    injectGtagScript(id);
    isInitialized = true;
    
    if (isDev) {
      console.log('[Analytics] 🚀 Google Analytics 4 initialized successfully:', {
        measurementId: id,
        timestamp: new Date().toISOString(),
        dataLayerExists: !!window.dataLayer,
        gtagExists: typeof window.gtag === 'function'
      });
    }
  } catch (error) {
    console.error('[Analytics] ❌ Error initializing Google Analytics:', {
      error: error.message,
      stack: error.stack,
      measurementId: id
    });
    isInitialized = false;
  }
}

/**
 * Tracks a custom event in Google Analytics 4
 * @param {string} eventName - Name of the event
 * @param {Record<string, any>} params - Event parameters
 */
export function trackEvent(eventName, params = {}) {
  // Always log in development for debugging
  const isDev = process.env.REACT_APP_ENVIRONMENT === 'development';
  
  if (!isInitialized) {
    if (isDev) {
      console.warn('[Analytics] ⚠️ Analytics not initialized. Event not tracked:', {
        eventName,
        params,
        isInitialized,
        measurementId
      });
    }
    return;
  }

  if (!eventName || typeof eventName !== 'string') {
    console.warn('[Analytics] ⚠️ Event name must be a non-empty string. Received:', eventName);
    return;
  }

  try {
    if (window.gtag) {
      // Clean params: remove undefined values for cleaner GA4 events
      const cleanedParams = Object.keys(params).reduce((acc, key) => {
        if (params[key] !== undefined) {
          acc[key] = params[key];
        }
        return acc;
      }, {});
      
      // Send to GA4
      window.gtag('event', eventName, cleanedParams);
      
      // Detailed logging for debugging
      if (isDev) {
        console.log('[Analytics] ✅ Event tracked:', {
          eventName,
          params: cleanedParams,
          timestamp: new Date().toISOString(),
          measurementId
        });
        console.log('[Analytics] 📊 Full event data:', {
          event: eventName,
          ...cleanedParams
        });
      }
    } else {
      console.warn('[Analytics] ⚠️ gtag function not available. Window.gtag:', typeof window.gtag);
    }
  } catch (error) {
    console.error('[Analytics] ❌ Error tracking event:', {
      eventName,
      params,
      error: error.message,
      stack: error.stack
    });
  }
}

/**
 * Checks if Analytics is initialized
 * @returns {boolean}
 */
export function isAnalyticsInitialized() {
  return isInitialized;
}

